import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { LoadingButton } from 'src/components/buttons/LoadingButton';
import FormControl from '@mui/material/FormControl';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cleanEmail, isValidEmail } from 'src/util/emailUtils';
import type { AuthenticationFlowPageProps } from '../AuthenticationFlow';
import type { RootState } from '../store';
import { setEmail } from '../store/authSlice';
import { useAuth } from '../../../contexts/AuthContext';

type AuthenticationEnterEmailPageProps = AuthenticationFlowPageProps;

export function AuthenticationEnterEmailPage({
  pageId: pageID,
  onPageComplete,
}: AuthenticationEnterEmailPageProps) {
  const { handleEmailCheck } = useAuth();
  const dispatch = useDispatch();
  const startingEmail = useSelector((state: RootState) => {
    return state.email;
  });
  const [enteredEmail, setEnteredEmail] = useState(startingEmail || '');
  const [cleanedEmail, setCleanedEmail] = useState(startingEmail || '');
  const [shouldShowError, setShouldShowError] = useState(false);

  const onEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEnteredEmail(event.currentTarget.value);
    setCleanedEmail(cleanEmail(event.currentTarget.value.toLowerCase()));
    dispatch(setEmail(event.currentTarget.value));
    setShouldShowError(false);
  };

  const attemptSubmit = useCallback(async () => {
    setCleanedEmail(cleanEmail(enteredEmail.toLowerCase()));
    if (!isValidEmail(cleanedEmail)) {
      setShouldShowError(true);
    } else {
      const methods = await handleEmailCheck(cleanedEmail);
      const exists = methods.includes('password');
      // TODO: Figure out if we should take the user to 'email sign in' or to 'email sign up'
      // (check if an account on the email already exists)
      setShouldShowError(false);
      dispatch(setEmail(cleanedEmail));
      onPageComplete(pageID, exists ? 'email sign in' : 'email sign up');
    }
  }, [
    handleEmailCheck,
    enteredEmail,
    cleanedEmail,
    dispatch,
    onPageComplete,
    pageID,
  ]);

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if (event.code === 'Enter' || event.code === 'NumpadEnter') {
        event.preventDefault();
        attemptSubmit();
      }
    };
    document.addEventListener('keydown', listener);
    return () => {
      document.removeEventListener('keydown', listener);
    };
  }, [attemptSubmit]);

  if (!pageID || !onPageComplete) return null;

  // TODO: Abstract out consistent JSX
  return (
    <Stack spacing={'32px'} my={'64px'} width={'100%'} maxWidth={'420px'}>
      <Stack spacing={'8px'}>
        <Typography
          variant="h4"
          fontWeight="500"
          textTransform={'capitalize'}
          textAlign={'center'}
        >
          Enter Email Address
        </Typography>
        <Typography variant="body1" textAlign={'center'}>
          Let&apos;s see if you already have an account with us...
        </Typography>
      </Stack>
      <Stack textAlign={'center'} spacing={'12px'}>
        <FormControl>
          <TextField
            error={shouldShowError}
            helperText={
              shouldShowError
                ? 'Please enter a valid email address!'
                : undefined
            }
            label="Email Address"
            type={'email'}
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgb(192, 192, 192)',
              },
              mb: 4,
            }}
            fullWidth={true}
            value={enteredEmail}
            onChange={onEmailChange}
            autoFocus
          />
        </FormControl>
        {/* TODO <Box textAlign={'left'}>
            <Link href={'#'} color={theme.palette.text.secondary}>
              Forgot your email?
            </Link>
          </Box> */}
      </Stack>
      <Box textAlign={'center'}>
        <LoadingButton
          variant="contained"
          size="large"
          fullWidth
          onClick={attemptSubmit}
        >
          Continue
        </LoadingButton>
      </Box>
    </Stack>
  );
}
